import { PERMISSIONS } from '../constants/permissions';

export interface DecodedToken {
  iss: string;
  sub: string;
  permissoes: string[];
  usuario_id: number;
  exp: number;
}

// Função para decodificar JWT sem dependência externa
function decodeJWT(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Erro ao decodificar JWT:', error);
    return null;
  }
}

class PermissionManager {
  private permissions: any = {};
  private decodedToken: DecodedToken | null = null;

  // Decodifica o token JWT e extrai as permissões
  decodeToken(token: string): DecodedToken | null {
    try {
      const decoded = decodeJWT(token) as DecodedToken;
      this.decodedToken = decoded;
      
      // Converte array de permissões para objeto
      this.permissions = {};
      decoded.permissoes.forEach(permission => {
        this.permissions[permission as keyof any] = true;
      });
      
      console.log('🔐 Permissões decodificadas:', this.permissions);
      return decoded;
    } catch (error) {
      console.error('❌ Erro ao decodificar token:', error);
      return null;
    }
  }

  // Verifica se o usuário tem uma permissão específica
  hasPermission(permission: keyof any): boolean {
    return !!this.permissions[permission];
  }

  // Verifica se o usuário tem pelo menos uma das permissões
  hasAnyPermission(permissions: (keyof any)[]): boolean {
    return permissions.some(permission => this.hasPermission(permission));
  }

  // Verifica se o usuário tem todas as permissões
  hasAllPermissions(permissions: (keyof any)[]): boolean {
    return permissions.every(permission => this.hasPermission(permission));
  }

  // Retorna todas as permissões do usuário
  getAllPermissions(): any {
    return { ...this.permissions };
  }

  // Retorna o ID do usuário
  getUserId(): number | null {
    return this.decodedToken?.usuario_id || null;
  }

  // Retorna o nome do usuário
  getUsername(): string | null {
    return this.decodedToken?.sub || null;
  }

  // Verifica se o token está expirado
  isTokenExpired(): boolean {
    if (!this.decodedToken) return true;
    const currentTime = Math.floor(Date.now() / 1000);
    return this.decodedToken.exp < currentTime;
  }

  // Limpa as permissões (para logout)
  clearPermissions(): void {
    this.permissions = {};
    this.decodedToken = null;
  }

  // Mapeamento de permissões para funcionalidades
  canAccessCursos(): boolean {
    return this.hasAnyPermission([PERMISSIONS.CURSO, PERMISSIONS.GESTAO_CURSO, PERMISSIONS.PROFESSOR]);
  }

  canAccessTratamentos(): boolean {
    return this.hasAnyPermission([PERMISSIONS.GESTAO_TRATAMENTO, PERMISSIONS.ATENDENTE, PERMISSIONS.TRATAMENTO]);
  }

  canAccessAssociados(): boolean {
    return this.hasAnyPermission([PERMISSIONS.ADMINISTRATIVO, PERMISSIONS.ASSOCIADO_CONTATO]);
  }

  canAccessDebitos(): boolean {
    return this.hasAnyPermission([PERMISSIONS.GESTAO_DEBITO, PERMISSIONS.ADMINISTRATIVO]);
  }

  canAccessAgendamentos(): boolean {
    return this.hasAnyPermission([PERMISSIONS.TRATAMENTO, PERMISSIONS.GESTAO_AGENDA, PERMISSIONS.ATENDENTE]);
  }

  canManageCursos(): boolean {
    return this.hasPermission(PERMISSIONS.GESTAO_CURSO);
  }

  canManageTratamentos(): boolean {
    return this.hasPermission(PERMISSIONS.GESTAO_TRATAMENTO);
  }

  canManageDebitos(): boolean {
    return this.hasPermission(PERMISSIONS.GESTAO_DEBITO);
  }

  isSuperAdmin(): boolean {
  // return this.hasAllPermissions([
  //   'ADMINISTRATIVO',
  //   'ASSOCIADO_CONTATO',
  //   //   'ATENDENTE',
  //   'CURSO',
  //   'GESTAO_ASSOCIADO',
  //   'GESTAO_CURSO',
  //   'GESTAO_DEBITO',
  //   'GESTAO_TRATAMENTO',
  //   'JURIDICO',
  //   'NUCLEO',
  //   'PROFESSOR'
  // ]);
  return this.hasPermission(PERMISSIONS.SUPER_ADMINISTRADOR);
}

  isAdmin(): boolean {
    return this.hasPermission(PERMISSIONS.ADMINISTRATIVO);
  }

  isProfessor(): boolean {
    return this.hasPermission(PERMISSIONS.PROFESSOR);
  }

  isOnlyProfessor(): boolean {
    const allPerms = Object.entries(this.permissions);
    return (
      allPerms.length > 0 &&
      allPerms.every(([key, value]) => {
        return key === PERMISSIONS.PROFESSOR ? value === true : value === false;
      })
    );
  }

  notProfessor(): boolean {
    return !this.isProfessor();
  }

  isAtendente(): boolean {
    return this.hasPermission(PERMISSIONS.ATENDENTE);
  }

  isAssociadoContato(): boolean {
    return this.hasPermission(PERMISSIONS.ASSOCIADO_CONTATO);
  }

  // ─── Jurídico ─────────────────────────────────────────────────────────────

  canAccessJuridico(): boolean {
    return this.hasAnyPermission([
      PERMISSIONS.JURIDICO,
      PERMISSIONS.JURIDICO_INCLUIR_AUDIENCIA,
      PERMISSIONS.JURIDICO_EXCLUIR_AUDIENCIA,
      PERMISSIONS.JURIDICO_INCLUIR_PRAZO,
      PERMISSIONS.JURIDICO_EXCLUIR_PRAZO,
      PERMISSIONS.JURIDICO_INCLUIR_PRAZO_ANDAMENTO,
      PERMISSIONS.JURIDICO_PUBLICACAO,
      PERMISSIONS.JURIDICO_INCLUIR_PROCESSO,
      PERMISSIONS.JURIDICO_EXCLUIR_PROCESSO,
      PERMISSIONS.JURIDICO_INCLUIR_OBSERVACAO_PROCESSO,
      PERMISSIONS.JURIDICO_EXCLUIR_OBSERVACAO_PROCESSO,
      PERMISSIONS.JURIDICO_INCLUIR_ANDAMENTO_PROCESSO,
      PERMISSIONS.JURIDICO_EXCLUIR_ANDAMENTO_PROCESSO,
      PERMISSIONS.JURIDICO_INCLUIR_INICIAL,
      PERMISSIONS.JURIDICO_EXCLUIR_INICIAL,
      PERMISSIONS.JURIDICO_INCLUIR_CALCULO,
      PERMISSIONS.JURIDICO_EXCLUIR_CALCULO,
      PERMISSIONS.JURIDICO_INCLUIR_PRECATORIO,
      PERMISSIONS.JURIDICO_EXCLUIR_PRECATORIO,
      PERMISSIONS.JURIDICO_ATUALIZAR_STATUS,
      PERMISSIONS.JURIDICO_INCLUIR_ASSUNTO_ATENDIMENTO,
      PERMISSIONS.JURIDICO_RELATORIO_GERAL,
      PERMISSIONS.JURIDICO_RELATORIO_GERENCIAL,
      PERMISSIONS.JURIDICO_RELATORIO_FINANCEIRO,
      PERMISSIONS.JURIDICO_DEBITO,
      PERMISSIONS.JURIDICO_GESTAO_DEBITO,
      PERMISSIONS.JURIDICO_TERCEIRIZADO,
      PERMISSIONS.JURIDICO_ATENDENTE,
      PERMISSIONS.JURIDICO_GESTAO_AGENDA,
      PERMISSIONS.JURIDICO_AGENDAMENTO,
    ]);
  }

  isJuridicoTerceirizado(): boolean {
    return this.hasPermission(PERMISSIONS.JURIDICO_TERCEIRIZADO) && !this.isSuperAdmin();
  }

  isJuridicoAtendente(): boolean {
    return this.hasPermission(PERMISSIONS.JURIDICO_ATENDENTE) && !this.isSuperAdmin();
  }

  canManageJuridicoDebitos(): boolean {
    return this.hasPermission(PERMISSIONS.JURIDICO_GESTAO_DEBITO);
  }

  canViewJuridicoRelatorios(): boolean {
    return this.hasAnyPermission([
      PERMISSIONS.JURIDICO_RELATORIO_GERAL,
      PERMISSIONS.JURIDICO_RELATORIO_GERENCIAL,
      PERMISSIONS.JURIDICO_RELATORIO_FINANCEIRO,
    ]);
  }
}

// Instância singleton
export const permissionManager = new PermissionManager();
